import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { createExam, getExamDashboard } from 'utils/supabase';

// components

import ResumenSimulador from "components/Custom/ResumenSimulador";
import ButtonGroupNumPreguntas from "components/Custom/ButtonGroupNumPreguntas";
import ButtonGroupTiempo from "components/Custom/ButtonGroupTiempo";
import RadioGroupModo from "components/Custom/RadioGroupModo";
import ButtonGroupDificultad from "components/Custom/ButtonGroupDificultad";
import CheckGroupMaterias from "components/Custom/CheckGroupMaterias";
import BackdropAlert from "components/Custom/BackdropAlert.js";
import { Clipboard as ClipboardIcon, ArrowRight } from 'lucide-react';

export default function Examenes() {
  const [numPreguntas, setNumPreguntas] = useState(20);
  const [tiempo, setTiempo] = useState(0);
  const [modo, setModo] = useState("materias");
  const [dificultad, setDificultad] = useState(null);
  const [materiasSeleccionadas, setMateriasSeleccionadas] = useState([]);
  const [examDashboard, setExamDashboard] = useState({});
  const [backdropAlert, setBackdropAlert] = useState({
    open: false,
    severity: "info",
    title: "",
    message: "",
    description: "",
    closeOnBackdropClick: true,
    primaryAction: null,
    secondaryAction: null,
  });
  const history = useHistory();
  const opcionesNumeroPreguntas = [20, 50, 100, 140, 280];
  const opcionesTiempoMinutos = [0, 60, 120, 360];
  const mostrarModoEnarm = true;
  const mostrarModoSoloCasosClinicos = false;

  const isFormComplete =
  numPreguntas > 0 &&
  tiempo !== null &&
  modo &&
  materiasSeleccionadas.length > 0;

  const closeBackdropAlert = () => {
    setBackdropAlert((prev) => ({ ...prev, open: false }));
  };

  const showBackdropAlert = ({
    severity = "info",
    title = "",
    message = "",
    description = "",
    closeOnBackdropClick = true,
    primaryAction = null,
    secondaryAction = null,
  }) => {
    setBackdropAlert({
      open: true,
      severity,
      title,
      message,
      description,
      closeOnBackdropClick,
      primaryAction,
      secondaryAction,
    });
  };

  useEffect(() => {
    const fetchExamDashboard = async () => {
      try {
        const { data, error } = await getExamDashboard();
        if (error) {
          console.error('Error fetching exam dashboard:', error);
        } else {
          setExamDashboard(data);
        }
      } catch (error) {
        console.error('Unexpected error fetching exam dashboard:', error);
      }
    }
    fetchExamDashboard();
  }, []);

  const handleCrearExamen = async() => {
    if (!isFormComplete) {
      showBackdropAlert({
        severity: "warning",
        title: "Faltan campos",
        message: "Por favor, completa todos los campos antes de iniciar el simulador.",
        primaryAction: {
          label: "Entendido",
          onClick: closeBackdropAlert,
        },
      });
      return;
    }
    try {
    const { exam } = await createExam({
      selectedSpecialtyIds: materiasSeleccionadas,
      totalQuestions: numPreguntas,
      casePercentage: 0.4,
      difficultyFilter: dificultad || null,
      durationMinutes: tiempo && tiempo > 0 ? tiempo : null,
      modo: modo || 'aleatorio',
    });

    // React Router v5
    history.push(`/admin/simulador/${exam.id}`);
    } catch (error) {
      console.error('Error creating exam:', error);
      showBackdropAlert({
        severity: "error",
        title: "No se pudo crear el examen",
        message: error?.message || "Ocurrio un error inesperado.",
        primaryAction: {
          label: "Cerrar",
          onClick: closeBackdropAlert,
        },
      });
    }
  };

  return (
    <>
      <div className="flex flex-wrap mt-100 items-stretch">
        <div className="w-full lg:w-8/12 px-4 bg-white shadow-md rounded-lg border border-gray-200 p-4 flex flex-col">
          <div className="w-full mb-4">
            <h1 className="text-lg font-bold mb-2 flex items-center">
              <ClipboardIcon size={24} color="#6366f1" className="mr-2" />Crear simulador ENARM
            </h1>
          <p className="text-indigo-700 text-xs mb-4">
            Configura tu simulador ENARM a tu medida.
          </p>
          </div>
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="w-full mb-6 xl:mb-0 flex flex-col md:w-1/2 mr-4">
              <ButtonGroupNumPreguntas
                value={numPreguntas}
                onChange={setNumPreguntas}
                modo={modo}
                options={opcionesNumeroPreguntas}
                enarmValue={280}
              />
            </div>
            <div className="w-full mb-6 xl:mb-0 flex flex-col md:w-1/2">
              <ButtonGroupTiempo
                value={tiempo}
                onChange={setTiempo}
                modo={modo}
                options={opcionesTiempoMinutos}
                enarmValue={360}
              />
            </div>
          </div>
          <div className="flex flex-col gap-4 md:flex-row mt-6">
            <div className="w-full mb-6 xl:mb-0 flex flex-col md:w-1/2 mr-4">
              <RadioGroupModo
                value={modo}
                onChange={setModo}
                showAleatorioOption={false}
                showEnarmOption={mostrarModoEnarm}
                showSoloCasosClinicosOption={mostrarModoSoloCasosClinicos}
              />
            </div>
            <div className="w-full mb-6 xl:mb-0 flex flex-col md:w-1/2">
              <ButtonGroupDificultad value={dificultad} onChange={setDificultad} modo={modo} />
            </div>
          </div>
          <div className="flex flex-col gap-4 md:flex-row mt-6">
            <div className="w-full mb-6 xl:mb-0 flex flex-col md:mr-4">
              <CheckGroupMaterias onChange={setMateriasSeleccionadas} modo={modo} defaultSelectAll />
            </div>
          </div>
          <div className="flex flex-col gap-4 md:flex-row mt-6">
            <div className="w-full mb-6 xl:mb-0 flex justify-end md:w-full mr-4">
              <button
                className="bg-indigo-600 text-white text-sm px-4 py-3 rounded hover:bg-indigo-700 transition duration-300 inline-flex items-center justify-center gap-3 leading-none"
                onClick={handleCrearExamen}
              >
                Iniciar simulador
                <ArrowRight size={16} className="ml-1" />
              </button>
            </div>
          </div>
        </div>
        <div className="hidden lg:block lg:w-4/12 px-4">
          <ResumenSimulador examDashboard={examDashboard} />
        </div>
      </div>
      <BackdropAlert
        open={backdropAlert.open}
        onClose={closeBackdropAlert}
        severity={backdropAlert.severity}
        title={backdropAlert.title}
        message={backdropAlert.message}
        description={backdropAlert.description}
        closeOnBackdropClick={backdropAlert.closeOnBackdropClick}
        primaryAction={backdropAlert.primaryAction}
        secondaryAction={backdropAlert.secondaryAction}
      />
    </>
  );
}
